import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ClockIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  NoSymbolIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  PencilSquareIcon,
  BoltIcon,
  TrophyIcon,
  FireIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import Spinner from '../../components/ui/Spinner';
import ConfirmModal from '../../components/ui/ConfirmModal';
import {
  getPublicAssessment,
  startSubmission,
  saveAnswers,
  submitAssessment,
} from '../../api/publicApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(seconds) {
  if (seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function buildAnswersPayload(answers) {
  return Object.entries(answers)
    .filter(([, val]) => val !== '' && val !== null && val !== undefined)
    .map(([question_id, answer_text]) => ({
      question_id: Number(question_id),
      answer_text: String(answer_text),
    }));
}

function getLevel(pct) {
  if (pct >= 100) return { label: 'Champion',   color: 'text-amber-500',   bg: 'bg-amber-50  border-amber-200' };
  if (pct >= 75)  return { label: 'Achiever',   color: 'text-violet-600',  bg: 'bg-violet-50 border-violet-200' };
  if (pct >= 50)  return { label: 'Challenger', color: 'text-blue-600',    bg: 'bg-blue-50   border-blue-200' };
  if (pct >= 25)  return { label: 'Explorer',   color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' };
  return            { label: 'Starter',     color: 'text-neutral-500', bg: 'bg-neutral-50 border-neutral-200' };
}

function getMilestone(idx, total) {
  const pct = idx / total;
  if (idx === 0) return null;
  if (pct === 0.5)   return { msg: "Halfway there! Keep it up 🔥", color: 'text-orange-600 bg-orange-50 border-orange-200' };
  if (pct >= 0.75 && Math.round(pct * 10) === 8) return { msg: "Almost done! Final stretch 💪", color: 'text-violet-600 bg-violet-50 border-violet-200' };
  return null;
}

// ─── Error screens ─────────────────────────────────────────────────────────────

function ErrorScreen({ icon: Icon, iconBg, iconColor, title, message }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl text-center">
        <div className={clsx('mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full', iconBg)}>
          <Icon className={clsx('h-8 w-8', iconColor)} />
        </div>
        <h1 className="text-xl font-bold font-heading text-neutral-900 mb-2">{title}</h1>
        <p className="text-sm text-neutral-500 leading-relaxed">{message}</p>
      </div>
    </div>
  );
}

// ─── Public Header ─────────────────────────────────────────────────────────────

function PublicHeader() {
  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-neutral-100 shadow-sm">
      <div className="mx-auto max-w-3xl px-4 py-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
          <SparklesIcon className="h-4 w-4 text-white" />
        </div>
        <span className="text-base font-bold font-heading text-neutral-900 tracking-tight">
          FounderMind <span className="text-primary-600">AI</span>
        </span>
      </div>
    </header>
  );
}

// ─── Question Input ─────────────────────────────────────────────────────────────

function QuestionInput({ question, value, onChange, onBlur }) {
  const { question_type, options = [] } = question;
  const type = question_type === 'long_text'  ? 'textarea'
             : question_type === 'short_text' ? 'text'
             : question_type === 'rating'     ? 'scale'
             : question_type === 'scenario'   ? 'textarea'
             : question_type;

  if (type === 'text') {
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="input-base text-base"
        placeholder="Type your answer here…"
        autoFocus
      />
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        rows={5}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="input-base resize-y text-base leading-relaxed"
        placeholder="Share your thoughts here…"
        autoFocus
      />
    );
  }

  if (type === 'mcq') {
    return (
      <div className="space-y-3">
        {options.map((opt) => {
          const optVal    = typeof opt === 'object' ? opt.text ?? opt.option_text ?? opt.value : opt;
          const optId     = typeof opt === 'object' ? opt.option_id ?? opt.id ?? optVal : optVal;
          const isSelected = String(value) === String(optVal);
          return (
            <label
              key={optId}
              className={clsx(
                'flex cursor-pointer items-center gap-4 rounded-xl border-2 px-5 py-4 transition-all duration-150 select-none',
                isSelected
                  ? 'border-primary-500 bg-primary-50 shadow-md scale-[1.01]'
                  : 'border-neutral-200 bg-white hover:border-primary-300 hover:bg-primary-50/40 hover:scale-[1.005]'
              )}
              onClick={() => { onChange(optVal); onBlur && onBlur(); }}
            >
              <span className={clsx(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all',
                isSelected ? 'border-primary-600 bg-primary-600' : 'border-neutral-300 bg-white'
              )}>
                {isSelected && <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} />}
              </span>
              <span className={clsx('text-sm font-medium', isSelected ? 'text-primary-800' : 'text-neutral-700')}>
                {optVal}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  if (type === 'scale') {
    const current = Number(value);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const selected = current === n;
            const color = n <= 3 ? 'primary' : n <= 7 ? 'secondary' : 'success';
            return (
              <button
                key={n}
                type="button"
                onClick={() => { onChange(n); onBlur && onBlur(); }}
                className={clsx(
                  'flex h-12 w-full items-center justify-center rounded-xl border-2 text-sm font-bold transition-all duration-150',
                  selected
                    ? color === 'primary'   ? 'border-primary-600 bg-primary-600 text-white shadow-lg scale-110'
                    : color === 'secondary' ? 'border-secondary-600 bg-secondary-600 text-white shadow-lg scale-110'
                    :                         'border-success-600 bg-success-600 text-white shadow-lg scale-110'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-primary-400 hover:bg-primary-50 hover:scale-105'
                )}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-neutral-400 px-1">
          <span>Not at all</span>
          <span>Extremely</span>
        </div>
      </div>
    );
  }

  if (type === 'boolean') {
    const boolVal = value === null || value === undefined ? null : String(value).toLowerCase();
    return (
      <div className="flex gap-4">
        {['yes', 'no'].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => { onChange(opt); onBlur && onBlur(); }}
            className={clsx(
              'flex-1 rounded-xl border-2 py-4 text-base font-bold capitalize transition-all duration-150',
              boolVal === opt
                ? opt === 'yes'
                  ? 'border-success-500 bg-success-500 text-white shadow-lg scale-105'
                  : 'border-error-500 bg-error-500 text-white shadow-lg scale-105'
                : 'border-neutral-200 bg-white text-neutral-600 hover:border-primary-300 hover:bg-primary-50'
            )}
          >
            {opt === 'yes' ? '👍 Yes' : '👎 No'}
          </button>
        ))}
      </div>
    );
  }

  return (
    <textarea
      rows={5}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="input-base resize-y text-base"
      placeholder="Share your thoughts here…"
      autoFocus
    />
  );
}

// ─── Progress Dots ─────────────────────────────────────────────────────────────

function ProgressDots({ total, current, answers, questions }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-center">
      {questions.map((q, i) => {
        const qid     = q.question_id ?? q.id;
        const answered = answers[qid] !== undefined && answers[qid] !== '' && answers[qid] !== null;
        const isCurrent = i === current;
        return (
          <div
            key={i}
            className={clsx(
              'rounded-full transition-all duration-300',
              isCurrent
                ? 'h-3 w-6 bg-primary-600'
                : answered
                ? 'h-2.5 w-2.5 bg-emerald-500'
                : 'h-2.5 w-2.5 bg-neutral-200'
            )}
          />
        );
      })}
    </div>
  );
}

// ─── Review Screen ─────────────────────────────────────────────────────────────

function ReviewScreen({ questions, answers, onEdit, onSubmit, submitting }) {
  const unanswered = questions.filter((q) => {
    const v = answers[q.question_id ?? q.id];
    return v === undefined || v === '' || v === null;
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 p-6 text-white text-center">
        <TrophyIcon className="mx-auto h-10 w-10 mb-3 opacity-90" />
        <h2 className="text-xl font-extrabold font-heading">Review Your Answers</h2>
        <p className="mt-1 text-sm text-primary-100">
          {unanswered.length === 0
            ? 'All questions answered! You\'re ready to submit.'
            : `${unanswered.length} question${unanswered.length !== 1 ? 's' : ''} unanswered.`}
        </p>
      </div>

      {/* Question list */}
      <div className="space-y-3">
        {questions.map((q, i) => {
          const qid     = q.question_id ?? q.id;
          const val     = answers[qid];
          const answered = val !== undefined && val !== '' && val !== null;
          return (
            <div
              key={qid}
              className={clsx(
                'flex items-start gap-4 rounded-xl border p-4 transition-colors',
                answered ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
              )}
            >
              <div className={clsx(
                'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                answered ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'
              )}>
                {answered ? <CheckIcon className="h-4 w-4" /> : i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-800 leading-snug">{q.question_text}</p>
                {answered ? (
                  <p className="mt-1 text-xs text-neutral-600 truncate">
                    {String(val).length > 80 ? String(val).slice(0, 80) + '…' : String(val)}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-amber-600 font-medium">Not answered</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => onEdit(i)}
                className="shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-white hover:text-primary-600 transition-colors"
                title="Edit answer"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <div className="flex flex-col items-center gap-3 pt-2">
        <button
          type="button"
          disabled={submitting}
          onClick={onSubmit}
          className="w-full max-w-sm rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 py-3.5 text-sm font-bold text-white shadow-lg hover:from-primary-700 hover:to-secondary-700 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner size="sm" className="text-white" /> : <TrophyIcon className="h-4 w-4" />}
          {submitting ? 'Submitting…' : 'Submit Assessment'}
        </button>
        <p className="text-xs text-neutral-400 text-center">
          You won't be able to change answers after submitting.
        </p>
      </div>
    </div>
  );
}

// ─── Assessment Step (Gamified) ────────────────────────────────────────────────

function AssessmentStep({ assessment, submissionId, token, participantName, onSubmitted }) {
  const navigate  = useNavigate();
  const questions = assessment.questions ?? [];

  const [answers, setAnswers]         = useState({});
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [phase, setPhase]             = useState('questions'); // 'questions' | 'review'
  const [direction, setDirection]     = useState('forward');
  const [visible, setVisible]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [isSaving, setIsSaving]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [xpPop, setXpPop]             = useState(false);

  // Timer
  const [timeLeft, setTimeLeft]   = useState(
    assessment.time_limit_minutes ? assessment.time_limit_minutes * 60 : null
  );
  const autoSubmitFiredRef = useRef(false);
  const autoSaveIntervalRef = useRef(null);

  // ── Computed ──────────────────────────────────────────────────────────────
  const answeredCount = questions.filter((q) => {
    const v = answers[q.question_id ?? q.id];
    return v !== undefined && v !== '' && v !== null;
  }).length;
  const progressPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
  const xpEarned    = answeredCount * 100;
  const level       = getLevel(progressPct);
  const currentQ    = questions[currentIdx];
  const currentQid  = currentQ ? (currentQ.question_id ?? currentQ.id) : null;
  const currentVal  = currentQid != null ? (answers[currentQid] ?? '') : '';
  const isAnswered  = currentVal !== '' && currentVal !== null && currentVal !== undefined;
  const milestone   = getMilestone(currentIdx, questions.length);

  // ── Save helpers ──────────────────────────────────────────────────────────
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const doSave = useCallback(async (currentAnswers) => {
    const payload = buildAnswersPayload(currentAnswers);
    if (payload.length === 0 || !submissionId) return;
    try {
      setIsSaving(true);
      await saveAnswers(token, submissionId, { answers: payload });
    } catch { /* silent */ }
    finally { setIsSaving(false); }
  }, [token, submissionId]);

  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(() => doSave(answersRef.current), 30_000);
    return () => clearInterval(autoSaveIntervalRef.current);
  }, [doSave]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      if (!autoSubmitFiredRef.current) {
        autoSubmitFiredRef.current = true;
        toast('Time is up! Submitting…', { icon: '⏰' });
        handleSubmitFinal(answersRef.current, true);
      }
      return;
    }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const animateTo = (newIdx, dir) => {
    setDirection(dir);
    setVisible(false);
    setTimeout(() => {
      setCurrentIdx(newIdx);
      setVisible(true);
    }, 180);
  };

  const handleNext = () => {
    // Save on advance
    doSave({ ...answersRef.current });
    if (currentIdx < questions.length - 1) {
      animateTo(currentIdx + 1, 'forward');
    } else {
      // Last question → review
      setDirection('forward');
      setVisible(false);
      setTimeout(() => { setPhase('review'); setVisible(true); }, 180);
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) {
      animateTo(currentIdx - 1, 'back');
    }
  };

  const handleEditFromReview = (idx) => {
    setPhase('questions');
    setVisible(false);
    setDirection('back');
    setTimeout(() => { setCurrentIdx(idx); setVisible(true); }, 180);
  };

  // ── Answer change ─────────────────────────────────────────────────────────
  const handleChange = (val) => {
    if (!currentQid) return;
    const wasAnswered = answers[currentQid] !== undefined && answers[currentQid] !== '';
    setAnswers((prev) => ({ ...prev, [currentQid]: val }));
    // XP pop animation on first answer
    if (!wasAnswered) {
      setXpPop(true);
      setTimeout(() => setXpPop(false), 1000);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmitFinal = async (currentAnswers, autoSubmit = false) => {
    if (!submissionId) {
      toast.error('Session error — please refresh and try again.');
      return;
    }
    setSubmitting(true);
    clearInterval(autoSaveIntervalRef.current);
    try {
      const payload = buildAnswersPayload(currentAnswers);
      await submitAssessment(token, submissionId, { answers: payload });
      onSubmitted();
      navigate(`/a/${token}/thankyou`, {
        state: { assessmentTitle: assessment.title, participantName, autoSubmit },
      });
    } catch (err) {
      setSubmitting(false);
      toast.error(err?.response?.data?.message ?? 'Submission failed. Please try again.');
    }
  };

  const timerUrgent = timeLeft !== null && timeLeft <= 60;

  // ─── Review phase ────────────────────────────────────────────────────────
  if (phase === 'review') {
    return (
      <div className={clsx('transition-all duration-200', visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4')}>
        <ReviewScreen
          questions={questions}
          answers={answers}
          onEdit={handleEditFromReview}
          onSubmit={() => handleSubmitFinal(answers)}
          submitting={submitting}
        />
      </div>
    );
  }

  // ─── Questions phase ─────────────────────────────────────────────────────
  return (
    <div className="min-h-[calc(100vh-53px)] flex flex-col">

      {/* ── Top bar: level + XP + timer ── */}
      <div className="sticky top-[53px] z-10 bg-white/95 backdrop-blur border-b border-neutral-100 shadow-sm">
        <div className="mx-auto max-w-2xl px-4 py-3 space-y-2">
          {/* Row 1: level / XP / timer */}
          <div className="flex items-center justify-between gap-3">
            <div className={clsx('flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-all', level.bg, level.color)}>
              <BoltIcon className="h-3.5 w-3.5" />
              {level.label}
            </div>

            <div className="flex items-center gap-3">
              {/* XP counter */}
              <div className={clsx('relative flex items-center gap-1 text-xs font-bold text-amber-600 transition-transform', xpPop && 'scale-125')}>
                <TrophyIcon className="h-4 w-4" />
                <span>{xpEarned} XP</span>
                {xpPop && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-emerald-500 animate-bounce">
                    +100
                  </span>
                )}
              </div>

              {/* Timer */}
              {timeLeft !== null && (
                <span className={clsx('flex items-center gap-1 text-sm font-mono font-bold', timerUrgent ? 'text-error-600 animate-pulse' : 'text-neutral-600')}>
                  <ClockIcon className="h-4 w-4" />
                  {formatTime(timeLeft)}
                </span>
              )}

              {/* Saving indicator */}
              {isSaving && <span className="text-xs text-neutral-400"><Spinner size="sm" className="text-neutral-400" /></span>}
            </div>
          </div>

          {/* Row 2: progress dots */}
          <ProgressDots
            total={questions.length}
            current={currentIdx}
            answers={answers}
            questions={questions}
          />

          {/* Row 3: progress text */}
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span>{answeredCount} of {questions.length} answered</span>
            <span className="font-semibold text-primary-600">{progressPct}% complete</span>
          </div>

          {/* Thin progress bar */}
          <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Main question card ── */}
      <div className="flex flex-1 items-start justify-center px-4 py-8">
        <div className="w-full max-w-2xl space-y-4">

          {/* Milestone banner */}
          {milestone && (
            <div className={clsx('flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold', milestone.color)}>
              <FireIcon className="h-4 w-4 shrink-0" />
              {milestone.msg}
            </div>
          )}

          {/* Question card */}
          <div
            className={clsx(
              'rounded-2xl bg-white shadow-xl border border-neutral-100 p-6 sm:p-8 transition-all duration-200',
              visible
                ? 'opacity-100 translate-x-0 scale-100'
                : direction === 'forward'
                  ? 'opacity-0 translate-x-8 scale-[0.98]'
                  : 'opacity-0 -translate-x-8 scale-[0.98]'
            )}
          >
            {/* Question meta */}
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-primary-600 px-3 py-1 text-xs font-bold text-white">
                  Q{currentIdx + 1}
                </span>
                {currentQ?.category && (
                  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-medium text-neutral-500 capitalize">
                    {currentQ.category}
                  </span>
                )}
              </div>
              <span className="text-xs font-medium text-neutral-400">
                {currentIdx + 1} / {questions.length}
              </span>
            </div>

            {/* Question text */}
            <p className="mb-6 text-lg font-bold text-neutral-900 leading-snug sm:text-xl">
              {currentQ?.question_text}
              {currentQ?.is_required && (
                <span className="ml-1 text-error-500">*</span>
              )}
            </p>

            {/* Difficulty badge */}
            {currentQ?.difficulty_level && (
              <div className="mb-4 flex items-center gap-1.5 text-xs text-neutral-500">
                <span>Difficulty:</span>
                <span className={clsx(
                  'rounded-full px-2 py-0.5 font-semibold capitalize',
                  currentQ.difficulty_level === 'basic'    ? 'bg-emerald-100 text-emerald-700' :
                  currentQ.difficulty_level === 'moderate' ? 'bg-amber-100 text-amber-700' :
                                                              'bg-red-100 text-red-700'
                )}>
                  {currentQ.difficulty_level}
                </span>
              </div>
            )}

            {/* Input */}
            {currentQ && (
              <QuestionInput
                question={currentQ}
                value={currentVal}
                onChange={handleChange}
                onBlur={() => doSave({ ...answersRef.current, [currentQid]: currentVal })}
              />
            )}

            {/* Answered checkmark */}
            {isAnswered && (
              <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                <CheckIcon className="h-4 w-4" />
                Answer saved
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentIdx === 0}
              className="flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-30 transition-all"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back
            </button>

            <button
              type="button"
              onClick={handleNext}
              className={clsx(
                'flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all',
                isAnswered
                  ? 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 scale-[1.02]'
                  : 'bg-neutral-400 hover:bg-neutral-500'
              )}
            >
              {currentIdx === questions.length - 1 ? (
                <>Review & Submit <TrophyIcon className="h-4 w-4" /></>
              ) : (
                <>Next <ChevronRightIcon className="h-4 w-4" /></>
              )}
            </button>
          </div>

          {/* Skip hint */}
          {!isAnswered && currentIdx < questions.length - 1 && (
            <p className="text-center text-xs text-neutral-400">
              You can skip this and come back later.
            </p>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => handleSubmitFinal(answers)}
        title="Submit assessment?"
        message="Are you sure? You can't change your answers after submitting."
        confirmLabel="Yes, Submit"
        confirmVariant="primary"
        loading={submitting}
      />
    </div>
  );
}

// ─── Registration Step ─────────────────────────────────────────────────────────

function RegistrationStep({ assessment, onStart }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  return (
    <div className="mx-auto w-full max-w-md px-4 py-10">
      <div className="rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <SparklesIcon className="h-3.5 w-3.5" />
            FounderMind AI Assessment
          </span>
        </div>

        <h1 className="mb-2 text-center text-2xl font-bold font-heading text-neutral-900">
          {assessment.title}
        </h1>
        {assessment.description && (
          <p className="mb-6 text-center text-sm text-neutral-500 leading-relaxed">
            {assessment.description}
          </p>
        )}

        <div className="mb-6 flex flex-wrap justify-center gap-4 text-xs text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="font-medium text-neutral-700">{assessment.questions?.length ?? 0}</span> questions
          </span>
          {assessment.time_limit_minutes && (
            <span className="flex items-center gap-1">
              <ClockIcon className="h-3.5 w-3.5" />
              <span className="font-medium text-neutral-700">{assessment.time_limit_minutes}</span> min
            </span>
          )}
          <span className="flex items-center gap-1">
            <TrophyIcon className="h-3.5 w-3.5" />
            <span className="font-medium text-neutral-700">{(assessment.questions?.length ?? 0) * 100}</span> XP to earn
          </span>
        </div>

        <form onSubmit={handleSubmit(onStart)} className="space-y-4">
          <div>
            <label className="label-base">Full Name <span className="text-error-500">*</span></label>
            <input
              type="text"
              className={clsx('input-base', errors.full_name && 'border-error-400')}
              placeholder="Jane Doe"
              {...register('full_name', { required: 'Full name is required' })}
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-error-600 flex items-center gap-1">
                <ExclamationCircleIcon className="h-3.5 w-3.5" /> {errors.full_name.message}
              </p>
            )}
          </div>

          <div>
            <label className="label-base">Email Address <span className="text-error-500">*</span></label>
            <input
              type="email"
              className={clsx('input-base', errors.email && 'border-error-400')}
              placeholder="jane@startup.com"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-error-600 flex items-center gap-1">
                <ExclamationCircleIcon className="h-3.5 w-3.5" /> {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="label-base">Startup Name</label>
            <input
              type="text"
              className="input-base"
              placeholder="Acme Inc. (optional)"
              {...register('startup_name')}
            />
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full mt-2 rounded-full">
            {isSubmitting ? <Spinner size="sm" className="text-white" /> : <SparklesIcon className="h-4 w-4" />}
            {isSubmitting ? 'Starting…' : 'Start Assessment'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AssessmentPage() {
  const { token } = useParams();
  const [loading, setLoading]         = useState(true);
  const [assessment, setAssessment]   = useState(null);
  const [error, setError]             = useState(null);
  const [step, setStep]               = useState('register');
  const [submissionId, setSubmissionId] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const anonStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicAssessment(token);
        if (!cancelled) setAssessment(res.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg    = err?.response?.data?.message ?? '';
        if (status === 404)                          setError('not_found');
        else if (status === 409 || /already submitted/i.test(msg)) setError('already_submitted');
        else if (/max attempt/i.test(msg))           setError('max_attempts');
        else                                         setError('generic');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleStart = async (formData) => {
    try {
      const payload = assessment.is_anonymous
        ? {}
        : { full_name: formData.full_name, email: formData.email, startup_name: formData.startup_name || undefined };
      const res     = await startSubmission(token, payload);
      const resData = res.data?.submission_id != null ? res.data : (res.data?.data ?? res.data);
      setSubmissionId(resData.submission_id);
      setParticipantName(formData.full_name ?? '');
      setStep('taking');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.message ?? '';
      if (status === 409 || /already submitted/i.test(msg)) setError('already_submitted');
      else if (/max attempt/i.test(msg)) setError('max_attempts');
      else toast.error(msg || 'Could not start the assessment. Please try again.');
    }
  };

  useEffect(() => {
    if (!assessment || !assessment.is_anonymous || step !== 'register' || anonStartedRef.current) return;
    anonStartedRef.current = true;
    handleStart({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="flex flex-col items-center gap-3 text-primary-600">
          <Spinner size="xl" />
          <p className="text-sm font-medium text-neutral-500">Loading assessment…</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found')        return <ErrorScreen icon={NoSymbolIcon}          iconBg="bg-neutral-100"  iconColor="text-neutral-400"  title="Assessment Not Found"       message="This link is invalid or has expired." />;
  if (error === 'already_submitted') return <ErrorScreen icon={LockClosedIcon}        iconBg="bg-primary-100"  iconColor="text-primary-500"  title="Already Submitted"          message="You have already submitted a response to this assessment." />;
  if (error === 'max_attempts')      return <ErrorScreen icon={ExclamationCircleIcon} iconBg="bg-warning-100"  iconColor="text-warning-600"  title="Maximum Attempts Reached"   message="You've reached the maximum number of attempts." />;
  if (error === 'generic')           return <ErrorScreen icon={ExclamationCircleIcon} iconBg="bg-error-100"    iconColor="text-error-600"    title="Something Went Wrong"       message="We couldn't load this assessment. Please refresh." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50/60 to-secondary-50/60">
      <PublicHeader />

      {step === 'register' && (
        assessment?.is_anonymous
          ? <div className="flex min-h-[calc(100vh-53px)] items-center justify-center"><Spinner size="xl" className="text-primary-500" /></div>
          : <RegistrationStep assessment={assessment} onStart={handleStart} />
      )}

      {step === 'taking' && (
        <AssessmentStep
          assessment={assessment}
          submissionId={submissionId}
          token={token}
          participantName={participantName}
          onSubmitted={() => {}}
        />
      )}
    </div>
  );
}
